import { Button, Card, CardContent, Grid } from "@mui/material";
import SettingsReport from "./SettingsReport";
import {
  PerformanceReportTypeEnum,
  ReportValueCardProps,
  PDFReportRequest,
  PDFReportClientInfoItem,
  PDFReportThemeInfoItem,
  PDFReportValueItem,
  PDFReportChartDataPoint,
  ReportPdfResponse,
  OrionTransaction,
} from "helpers/types";
import ChartsReport from "./ChartsReport";
import type { ChartDataPoint } from "./ChartDataModal";
import { SingleValueCardColor, SingleValueCardType } from "constants/index";
import SingleValueCard from "./SingleValueCard";
import MultiValuesCard from "./MultiValuesCard";
import CurrentAllocationChartReport from "./CurrentAllocationChartReport";
import { DrawdownComparisonChart } from "./DrawdownComparisonChart";
import { useStrategyReportValue } from "hooks/useReportStorage";
import { useMemo, useState, useCallback } from "react";
import { useGetReportPdfMutation } from "features/trading/accounts";
import {
  ReportSettingsProvider,
  useReportSettings,
} from "./ReportSettingsContext";
import TransactionModal from "./TransactionModal";

function StrategyReportContent() {
  // SingleValueCard hooks
  const [startingValue, setStartingValue] = useStrategyReportValue(
    "starting_value_strategy",
    10000000,
  );
  const [endingValue, setEndingValue] = useStrategyReportValue(
    "ending_value_strategy",
    10450000,
  );
  const [netChange, setNetChange] = useStrategyReportValue(
    "net_change_strategy",
    450000,
  );
  const [timeWeightedReturn, setTimeWeightedReturn] = useStrategyReportValue(
    "time_weighted_return_strategy",
    4.5,
  );
  const [incomeReceived, setIncomeReceived] = useStrategyReportValue(
    "income_received_strategy",
    125000,
  );
  const [marginInterest, setMarginInterest] = useStrategyReportValue(
    "margin_interest_strategy",
    15000,
  );

  // MultiValuesCard hooks - Keep
  const [sharesStart, setSharesStart] = useStrategyReportValue(
    "shares_start_strategy",
    100000,
  );
  const [sharesEnd, setSharesEnd] = useStrategyReportValue(
    "shares_end_strategy",
    100000,
  );
  const [valueStart, setValueStart] = useStrategyReportValue(
    "value_start_strategy",
    5000000,
  );
  const [valueEnd, setValueEnd] = useStrategyReportValue(
    "value_end_strategy",
    5200000,
  );
  const [dividends, setDividends] = useStrategyReportValue(
    "dividends_strategy",
    45000,
  );
  const [upsideParticipation, setUpsideParticipation] = useStrategyReportValue(
    "upside_participation_strategy",
    95,
  );
  const [basis, setBasis] = useStrategyReportValue("basis_strategy", 500000);
  const [taxSavings, setTaxSavings] = useStrategyReportValue(
    "tax_savings_strategy",
    1500000,
  );

  // MultiValuesCard hooks - Protect
  const [maxStockDrop, setMaxStockDrop] = useStrategyReportValue(
    "max_stock_drop_strategy",
    -15,
  );
  const [oeaDrop, setOeaDrop] = useStrategyReportValue("oea_drop_strategy", -8);
  const [collarPL, setCollarPL] = useStrategyReportValue(
    "collar_pl_strategy",
    75000,
  );
  const [availableMarginBalance, setAvailableMarginBalance] =
    useStrategyReportValue("available_margin_balance_strategy", 150000);

  // MultiValuesCard hooks - Diversify
  const [overallDiversifiedPerformance, setOverallDiversifiedPerformance] =
    useStrategyReportValue("overall_diversified_performance_strategy", 4.3);
  const [taxLossHarvestSleeve, setTaxLossHarvestSleeve] =
    useStrategyReportValue("tax_loss_harvest_sleeve_strategy", 0.3);
  const [beginningConcentration, setBeginningConcentration] =
    useStrategyReportValue("beginning_concentration_strategy", 85);
  const [currentConcentration, setCurrentConcentration] =
    useStrategyReportValue("current_concentration_strategy", 20);
  const [holdings, setHoldings] = useStrategyReportValue(
    "holdings_strategy",
    12,
  );

  // MultiValuesCard hooks - Harvest Tax Benefits
  const [realizedLosses, setRealizedLosses] = useStrategyReportValue(
    "realized_losses_strategy",
    150000,
  );
  const [lossesUsed, setLossesUsed] = useStrategyReportValue(
    "losses_used_strategy",
    50000,
  );
  const [poolRemaining, setPoolRemaining] = useStrategyReportValue(
    "pool_remaining_strategy",
    100000,
  );
  const [unrealizedGains, setUnrealizedGains] = useStrategyReportValue(
    "unrealized_gains_strategy",
    250000,
  );
  const [sharesSold, setSharesSold] = useStrategyReportValue(
    "shares_sold_strategy",
    5000,
  );
  const [sellableToday, setSellableToday] = useStrategyReportValue(
    "sellable_today_strategy",
    15000,
  );

  // Update keepValues with hooks
  const keepValuesWithHooks: ReportValueCardProps[] = [
    {
      label: "Shares (Start)",
      value: sharesStart,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSharesStart,
    },
    {
      label: "Shares (End)",
      value: sharesEnd,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSharesEnd,
    },
    {
      label: "Value (Start)",
      value: valueStart,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setValueStart,
    },
    {
      label: "Value (End)",
      value: valueEnd,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setValueEnd,
    },
    {
      label: "Dividends",
      value: dividends,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setDividends,
    },
    {
      label: "Upside Participation",
      value: upsideParticipation,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setUpsideParticipation,
    },
    {
      label: "Basis",
      value: basis,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setBasis,
    },
    {
      label: "Tax Savings",
      value: taxSavings,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setTaxSavings,
    },
  ];

  // Update protectValues with hooks
  const protectValuesWithHooks: ReportValueCardProps[] = [
    {
      label: "Max Stock Drop",
      value: maxStockDrop,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setMaxStockDrop,
    },
    {
      label: "OEA Drop",
      value: oeaDrop,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setOeaDrop,
    },
    {
      label: "Collar P&L",
      value: collarPL,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setCollarPL,
    },
    {
      label: "Available Margin Balance",
      value: availableMarginBalance,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setAvailableMarginBalance,
    },
  ];

  // Update diversifyValues with hooks
  const diversifyValuesWithHooks: ReportValueCardProps[] = [
    {
      label: "Overall Diversified Performance",
      value: overallDiversifiedPerformance,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setOverallDiversifiedPerformance,
    },
    {
      label: "Tax-Loss Harvest Sleeve",
      value: taxLossHarvestSleeve,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setTaxLossHarvestSleeve,
    },
    {
      label: "Beginning Concentration",
      value: beginningConcentration,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setBeginningConcentration,
    },
    {
      label: "Current Concentration",
      value: currentConcentration,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setCurrentConcentration,
    },
    {
      label: "Holdings",
      value: holdings,
      type: SingleValueCardType.NUMBER,
      onValueChange: setHoldings,
    },
  ];

  // Update harvestTaxBenefitsValues with hooks
  const harvestTaxBenefitsValuesWithHooks: ReportValueCardProps[] = [
    {
      label: "Realized Losses",
      value: realizedLosses,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setRealizedLosses,
    },
    {
      label: "Losses Used",
      value: lossesUsed,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setLossesUsed,
    },
    {
      label: "Pool Remaining",
      value: poolRemaining,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setPoolRemaining,
    },
    {
      label: "Unrealized Gains",
      value: unrealizedGains,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setUnrealizedGains,
    },
    {
      label: "Shares Sold",
      value: sharesSold,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSharesSold,
    },
    {
      label: "Sellable Today",
      value: sellableToday,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSellableToday,
    },
  ];

  const [chartDataPoints, setChartDataPoints] = useState<ChartDataPoint[]>([]);
  const [drawdownChartLimit, setDrawdownChartLimit] = useState(20);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [deselectedTransactions, setDeselectedTransactions] = useState<
    OrionTransaction[]
  >([]);
  const [getReportPdf, { isLoading: isPdfLoading }] = useGetReportPdfMutation();
  const { settingsInfo } = useReportSettings();

  const cardTypeToPdfType = useCallback(
    (
      type: SingleValueCardType,
      label: string,
    ): "number" | "currency" | "percentage" | "shares" => {
      if (label.toLowerCase().includes("share")) return "shares";
      return type as "number" | "currency" | "percentage";
    },
    [],
  );

  const themedCardsConfig = useMemo(
    () => [
      {
        label: "Keep",
        description: "keep",
        values: keepValuesWithHooks,
        addictional_component: null,
      },
      {
        label: "Protect",
        description: "protect",
        values: protectValuesWithHooks,
        addictional_component: {
          type: "bar" as const,
          title: "Drawdown Comparison",
          limit: drawdownChartLimit,
          data: [
            {
              label: "Stock",
              value: Math.abs(
                typeof maxStockDrop === "number"
                  ? maxStockDrop
                  : parseFloat(String(maxStockDrop)) || 0,
              ),
              type: "percentage" as const,
            },
            {
              label: "OEA",
              value: Math.abs(
                typeof oeaDrop === "number"
                  ? oeaDrop
                  : parseFloat(String(oeaDrop)) || 0,
              ),
              type: "percentage" as const,
            },
          ],
        },
      },
      {
        label: "Diversify",
        description: "diversify",
        values: diversifyValuesWithHooks,
        addictional_component: {
          type: "donut" as const,
          title: "Current Allocation",
          limit: 100,
          data: [
            {
              label: "Stock",
              value: currentConcentration as number,
              type: "percentage" as const,
            },
            {
              label: "Strategy",
              value: 100 - (currentConcentration as number),
              type: "percentage" as const,
            },
          ],
        },
      },
      {
        label: "Harvest",
        description: "harvest",
        values: harvestTaxBenefitsValuesWithHooks,
        addictional_component: null,
      },
    ],
    [
      keepValuesWithHooks,
      protectValuesWithHooks,
      diversifyValuesWithHooks,
      harvestTaxBenefitsValuesWithHooks,
      drawdownChartLimit,
      maxStockDrop,
      oeaDrop,
      currentConcentration,
    ],
  );

  const buildPdfRequest = useCallback((): PDFReportRequest => {
    const chart_data: PDFReportChartDataPoint[] = chartDataPoints.map((p) => ({
      date: p.date instanceof Date ? p.date.toISOString() : String(p.date),
      total: Number(p.total),
    }));

    const client_info: PDFReportClientInfoItem[] = [
      {
        label: "Starting Value",
        value: String(startingValue),
        type: "currency",
        description: "Beginning balance",
      },
      {
        label: "Ending Value",
        value: String(endingValue),
        type: "currency",
        description: "Ending balance",
      },
      {
        label: "Net Change",
        value: String(netChange),
        type: "currency",
        description: "Absolute change",
      },
      {
        label: "Time-Wgt Return",
        value: String(timeWeightedReturn),
        type: "percentage",
        description: "Net of fees",
      },
      {
        label: "Income Received",
        value: String(incomeReceived),
        type: "currency",
        description: "Divs & Option Premium",
      },
      {
        label: "Margin Interest",
        value: String(marginInterest),
        type: "currency",
        description: "Cost of borrowing",
      },
    ];

    const toPdfValues = (items: ReportValueCardProps[]): PDFReportValueItem[] =>
      items.map((item) => ({
        label: item.label,
        value: Number(item.value),
        type: cardTypeToPdfType(item.type, item.label),
      }));

    const theme_info: PDFReportThemeInfoItem[] = themedCardsConfig.map(
      (card) => ({
        label: card.label,
        description: card.description,
        values: toPdfValues(card.values),
        addictional_component: card.addictional_component,
      }),
    );

    return {
      type: "strategy",
      settings_info: settingsInfo,
      chart_data,
      client_info,
      theme_info,
    };
  }, [
    chartDataPoints,
    themedCardsConfig,
    settingsInfo,
    startingValue,
    endingValue,
    netChange,
    timeWeightedReturn,
    incomeReceived,
    marginInterest,
    cardTypeToPdfType,
  ]);

  const handleDownloadPdf = useCallback(async () => {
    try {
      const response: ReportPdfResponse =
        await getReportPdf(buildPdfRequest()).unwrap();
      if (response.download_url) {
        const a = document.createElement("a");
        a.href = response.download_url;
        a.click();
        a.remove();
        return;
      }
      throw new Error("Failed to download PDF");
    } catch (error) {
      throw new Error("Failed to download PDF");
    }
  }, [getReportPdf, buildPdfRequest]);

  const openTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(true);
  }, []);

  const closeTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(false);
  }, []);

  const handleCalculate = useCallback(() => {
    console.log("Transactions payload to send:", deselectedTransactions);
  }, [deselectedTransactions]);

  return (
    <Card>
      <CardContent>
        <SettingsReport
          type={PerformanceReportTypeEnum.STRATEGY}
          header="Optic Equity Access Performance"
          subtitle="OPTIC ASSET MANAGEMENT"
        />
        <Grid container justifyContent="center" sx={{ mt: 1 }}>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPdf}
              disabled={isPdfLoading}
            >
              {isPdfLoading ? "Generating…" : "Download PDF"}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCalculate}
              disabled={isPdfLoading}
            >
              {isPdfLoading ? "Calculating…" : `Calculate`}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={openTransactionModal}
              disabled={isPdfLoading}
            >
              {isPdfLoading ? "Updating…" : "Transactions"}
            </Button>
          </Grid>
        </Grid>
        <ChartsReport
          chartDataPoints={chartDataPoints}
          onChartDataChange={setChartDataPoints}
        />
        <Grid container spacing={4} columns={{ lg: 12, md: 6, xs: 12 }}>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={startingValue as number}
              label="Starting Value"
              description="Beginning balance"
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setStartingValue}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={endingValue as number}
              label="Ending value"
              description="Ending balance"
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setEndingValue}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={netChange as number}
              label="Net Change"
              description="Absolute change"
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setNetChange}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={timeWeightedReturn as number}
              label="Time-Wgt Return*"
              description="Net of fees"
              type={SingleValueCardType.PERCENTAGE}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setTimeWeightedReturn}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={incomeReceived as number}
              label="Income Received"
              description="Divs & Option Premium"
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setIncomeReceived}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={marginInterest as number}
              label="Margin Interest"
              description="Cost of borrowing"
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.ERROR}
              onValueChange={setMarginInterest}
            />
          </Grid>
        </Grid>
        <Grid container spacing={4} columns={{ lg: 12, md: 6, xs: 12 }}>
          {themedCardsConfig.map((card, index) => (
            <Grid item lg={6} xs={12} md={6} key={card.label}>
              <MultiValuesCard
                title={card.label}
                values={card.values}
                description={
                  index === 0
                    ? "You remained an owner of your stock while the OEA structure managed risk and tax opportunities."
                    : index === 1
                      ? "Collars aim to reduce impact of severe declines but also limit upside during rallies."
                      : index === 2
                        ? "Gradually reducing reliance on single stock by introducing diversified return sources."
                        : "Tax figures are estimates only. Please review with your tax advisor."
                }
                additionalComponents={
                  index === 1 ? (
                    <DrawdownComparisonChart
                      values={protectValuesWithHooks}
                      limit={drawdownChartLimit}
                      onLimitChange={setDrawdownChartLimit}
                    />
                  ) : index === 2 ? (
                    <CurrentAllocationChartReport
                      value={currentConcentration as number}
                    />
                  ) : null
                }
              />
            </Grid>
          ))}
        </Grid>
        <TransactionModal
          open={isTransactionModalOpen}
          onClose={closeTransactionModal}
          settingsInfo={settingsInfo}
          onDeselectedTransactionsChange={setDeselectedTransactions}
        />
      </CardContent>
    </Card>
  );
}

export const StrategyReport = () => (
  <ReportSettingsProvider>
    <StrategyReportContent />
  </ReportSettingsProvider>
);

export default StrategyReport;
